/**
 * The signed-in user's onboarding state in the portal (AIC-302): loaded once from GET /me/onboarding, saved one
 * action at a time with PUT (optimistic: the UI moves at once, the server's answer replaces it). Learning progress
 * only; nothing here touches survey or workflow state.
 *
 * status
 *   loading   before the first answer: nothing opens by itself
 *   saved     the server holds the state; the welcome may open (the server says "auto")
 *   local     the API failed or 0036 isn't applied: progress is kept in this browser only (localStorage), and the
 *             welcome never opens by itself, so a failing API can't reopen it on every page
 *   preview   an admin's read-only analyst preview: no call is made at all, nothing is saved or stored, and the
 *             tour can't be started (Getting started stays readable)
 * Saves are serialized. A 409 (another tab saved first) takes the server's state and replays the action once; the
 * server merges forward only, so a completed tour or a dismissed tip is never lost. Sign-out or another account
 * remounts the provider (App keys it on the user), which clears everything held in memory.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { TOUR_STEP_IDS, type OnboardingAction, type TipId, type TourSource, type TourStepId } from "@swtracker/core/onboarding";
import { ApiError } from "../api/client.ts";
import { usePortal } from "../api/hooks.ts";
import { onboardingView, type OnboardingViewData } from "../api/schemas.ts";
import { navigate } from "../router.tsx";
import "./onboarding.css";

const currentHref = (): string => `${window.location.pathname}${window.location.search}${window.location.hash}`;

export type OnboardingStatus = "loading" | "saved" | "local" | "preview";

export const BLANK: OnboardingViewData = {
  version: 0, welcome: "help_only", welcome_seen: false, tour: { step: null, completed: false, exited: false }, dismissed_tips: [], help_opens: 0,
};

/** The view after one action: the server's rules, for the optimistic update and the browser-only fallback. */
export function reduceView(v: OnboardingViewData, a: OnboardingAction): OnboardingViewData {
  const tour = { ...v.tour };
  const out = { ...v, tour };
  switch (a.type) {
    case "welcome_shown": case "welcome_skipped":
      return { ...out, welcome: "help_only", welcome_seen: true };
    case "tour_start":
      tour.step = a.source === "resume" && v.tour.step ? v.tour.step : TOUR_STEP_IDS[0];
      tour.exited = false;
      return a.source === "welcome" ? { ...out, welcome: "help_only", welcome_seen: true } : out;
    case "tour_step":
      tour.step = a.step; tour.exited = false; return out;
    case "tour_complete":
      tour.step = null; tour.completed = true; tour.exited = false; return out;
    case "tour_exit":
      tour.exited = tour.step !== null; return out;
    case "tip_dismiss":
      return v.dismissed_tips.includes(a.tip) ? out : { ...out, dismissed_tips: [...v.dismissed_tips, a.tip] };
    case "help_open":
      return { ...out, help_opens: Math.min(v.help_opens + 1, 10_000) };
  }
}

/** The browser-only copy, per user (a hash of the UPN, never the UPN itself). */
export function storageKey(email: string): string {
  let h = 0x811c9dc5;
  for (const ch of email.toLowerCase()) h = Math.imul(h ^ ch.charCodeAt(0), 0x01000193) >>> 0;
  return `swt-onboarding:v1:${h.toString(16)}`;
}
function readLocal(key: string): OnboardingViewData {
  try {
    const parsed = onboardingView.safeParse(JSON.parse(window.localStorage.getItem(key) ?? "null"));
    return parsed.success ? { ...parsed.data, welcome: "help_only" } : BLANK;
  } catch { return BLANK; }
}
function writeLocal(key: string, v: OnboardingViewData): void {
  try { window.localStorage.setItem(key, JSON.stringify({ ...v, welcome: "help_only" })); } catch { /* storage blocked: progress lasts this visit */ }
}

export interface TourRun { readonly step: TourStepId }

export interface OnboardingContextValue {
  readonly status: OnboardingStatus;
  readonly view: OnboardingViewData;
  readonly buildMode: boolean;
  /** Preview: nothing can be started or saved. */
  readonly readOnly: boolean;
  /** The tour on screen now (local to this tab), or null. */
  readonly tour: TourRun | null;
  startTour(source: TourSource): void;
  goToStep(step: TourStepId): void;
  exitTour(): void;
  completeTour(): void;
  dismissTip(tip: TipId): void;
  /** Records the welcome (shown / skipped) and the Help counter. */
  act(action: OnboardingAction): void;
  /** Goes up each time Getting started asks to see the welcome again (which records nothing). */
  readonly welcomeRequests: number;
  showWelcome(): void;
  readonly welcomeVisible: boolean;
  hideWelcome(): void;
  readonly helpTourHost: HTMLDivElement | null;
  readonly helpWelcomeHost: HTMLDivElement | null;
  setHelpTourHost(host: HTMLDivElement | null): void;
  setHelpWelcomeHost(host: HTMLDivElement | null): void;
}

const Ctx = createContext<OnboardingContextValue | null>(null);

/** The onboarding state, or null outside the provider (a screen rendered on its own in a test). */
export const useOnboarding = (): OnboardingContextValue | null => useContext(Ctx);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { api, email, preview = false, buildMode = true } = usePortal();
  const key = storageKey(email);
  const [status, setStatus] = useState<OnboardingStatus>(preview ? "preview" : "loading");
  const [view, setView] = useState<OnboardingViewData>(BLANK);
  const [tour, setTour] = useState<TourRun | null>(null);
  const [welcomeRequests, setWelcomeRequests] = useState(0);
  const [welcomeVisible, setWelcomeVisible] = useState(false);
  const [helpTourHost, setHelpTourHost] = useState<HTMLDivElement | null>(null);
  const [helpWelcomeHost, setHelpWelcomeHost] = useState<HTMLDivElement | null>(null);
  /** The local route (path, query, hash) the tour was started on, restored on Exit and on Finish. */
  const origin = useRef<string | null>(null);
  const statusRef = useRef(status);
  statusRef.current = status;
  const server = useRef<OnboardingViewData>(BLANK);
  const pending = useRef<OnboardingAction[]>([]);
  const chain = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    if (preview) return;
    let live = true;
    api.onboarding().then(
      (v) => { if (!live) return; server.current = v; setView(v); setStatus("saved"); },
      () => { if (!live) return; setView(readLocal(key)); setStatus("local"); },
    );
    return () => { live = false; };
  }, [api, key, preview]);

  const goLocal = useCallback(() => {
    setStatus("local");
    setView((v) => { const local = { ...v, welcome: "help_only" as const }; writeLocal(key, local); return local; });
  }, [key]);

  const act = useCallback((action: OnboardingAction) => {
    const s = statusRef.current;
    if (s === "preview") {
      // Read-only: a dismissed tip hides for this visit, nothing else changes and nothing is saved.
      if (action.type === "tip_dismiss") setView((v) => reduceView(v, action));
      return;
    }
    setView((v) => { const next = reduceView(v, action); if (s === "local") writeLocal(key, next); return next; });
    if (s !== "saved") return;
    pending.current.push(action);
    chain.current = chain.current.then(async () => {
      if (statusRef.current !== "saved") return;
      const settle = (v: OnboardingViewData) => {
        server.current = v;
        pending.current.shift();
        setView(pending.current.reduce(reduceView, v));
      };
      try {
        settle(await api.saveOnboarding(server.current.version, action));
      } catch (e) {
        const current = e instanceof ApiError && e.status === 409 ? onboardingView.safeParse((e.body as { state?: unknown } | null)?.state) : null;
        if (current?.success) {
          try { settle(await api.saveOnboarding(current.data.version, action)); return; } catch { /* fall through */ }
        }
        pending.current = [];
        goLocal();
      }
    });
  }, [api, goLocal, key]);

  /** Back to the page (path, filters) the tour was started on, if the user moved away during it: on Exit and on
   * Finish alike. Always a local route: it is this tab's own location, never taken from a URL parameter. */
  const returnToOrigin = useCallback(() => {
    const back = origin.current;
    origin.current = null;
    if (back && back !== currentHref()) navigate(back);
  }, []);

  const readOnly = status === "preview";
  const value = useMemo<OnboardingContextValue>(() => ({
    status, view, buildMode, readOnly, tour,
    act,
    startTour: (source) => {
      if (readOnly) return;
      origin.current = currentHref();
      setTour({ step: source === "resume" && view.tour.step ? view.tour.step : TOUR_STEP_IDS[0] });
      act({ type: "tour_start", source });
    },
    goToStep: (step) => { setTour({ step }); act({ type: "tour_step", step }); },
    exitTour: () => {
      setTour(null);
      act({ type: "tour_exit" });
      returnToOrigin();
    },
    completeTour: () => {
      setTour(null);
      act({ type: "tour_complete" });
      returnToOrigin();
    },
    dismissTip: (tip) => act({ type: "tip_dismiss", tip }),
    welcomeRequests,
    welcomeVisible, helpTourHost, helpWelcomeHost, setHelpTourHost, setHelpWelcomeHost,
    hideWelcome: () => setWelcomeVisible(false),
    showWelcome: () => { setWelcomeVisible((visible) => !visible); setWelcomeRequests((n) => n + 1); },
  }), [status, view, buildMode, readOnly, tour, act, returnToOrigin, welcomeRequests, welcomeVisible, helpTourHost, helpWelcomeHost]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
